import {
  getV8UserEnvs,
  useDeleteV8UserEnvs,
  usePostV8UserEnvs,
} from '@/api-client/meta/queries/xm-user-env-controller/xm-user-env-controller';
import { UserEnvValue } from '@/api-client/meta/model';
import {
  getEnvValueByPatternFromMap,
  getEnvValueFromMap,
  getEnvValuesFromMap,
  mergeUserEnvDataIntoMap,
  userEnvDataToMap,
} from '@/common/utils/userEnv.utils';
import { createDevOnlyKeyTracker } from '@/common/utils/userEnvDev.utils';
import { defineStore } from 'pinia';
import { ref } from 'vue';

const createDevOnly = createDevOnlyKeyTracker;

export const useUserEnvStore = defineStore('userEnvStore', () => {
  const userEnvMap = ref<Map<string, string>>(new Map());
  const hasUserEnvInfo = ref(false);

  const fetchAllEnvData = (data?: UserEnvValue[]) => {
    hasUserEnvInfo.value = true;
    userEnvMap.value = userEnvDataToMap(data);
  };

  const addNewEnvData = (data?: UserEnvValue[]) => {
    userEnvMap.value = mergeUserEnvDataIntoMap(userEnvMap.value, data);
  };

  const fetchEnvDataByKeys = async (keys: string[], global = false) => {
    try {
      const { data } = await getV8UserEnvs({ keys, global });
      if (data?.data?.length) {
        userEnvMap.value = mergeUserEnvDataIntoMap(userEnvMap.value, data.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const { mutateAsync: deleteUserEnv } = useDeleteV8UserEnvs();

  /**
   * @param key 삭제할 유저 환경 변수 키
   * @param all 모든 유저 환경 변수 삭제 여부
   * @param global global/ custom table 구분
   * @returns 삭제 성공 여부
   */
  const clearUserEnvValue = async (key: string, all = false, global = false) => {
    try {
      await deleteUserEnv({ params: { key, all, global } });
      userEnvMap.value.delete(key);
      return true;
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  // [DEV ONLY] 최근 설정한 키 추적 기능
  const devOnly = createDevOnly(clearUserEnvValue);

  const { mutateAsync: setUserEnv } = usePostV8UserEnvs();

  const setEnvValue = async <T extends Record<string, any> | string>(
    newEnv: {
      key: string;
      value: T;
    }[],
    global = false,
  ) => {
    const convertedValue = newEnv.map(({ key, value }) => ({
      key,
      value: typeof value === 'string' ? value : JSON.stringify(value),
    }));
    try {
      await setUserEnv({ data: convertedValue, params: { global } });
      convertedValue.forEach(({ key, value }) => {
        userEnvMap.value.set(key, value);
        devOnly.trackKey(key);
      });
    } catch (e) {
      console.log(e);
    }
  };

  const getEnvValue = <T>(key?: string): T | null => {
    return getEnvValueFromMap<T>(userEnvMap.value, key);
  };

  const getEnvValues = (keys: string[]): { [key in string]: any | null }[] => {
    return getEnvValuesFromMap(userEnvMap.value, keys) as { [key in string]: any | null }[];
  };

  const getEnvValueByPattern = (word: string): { [key in string]: any | null } => {
    return getEnvValueByPatternFromMap(userEnvMap.value, word) as {
      [key in string]: any | null;
    };
  };

  const initUserEnvMap = () => {
    hasUserEnvInfo.value = false;
    userEnvMap.value.clear();
    devOnly.clearAll();
  };

  return {
    hasUserEnvInfo,
    fetchAllEnvData,
    addNewEnvData,
    fetchEnvDataByKeys,
    clearUserEnvValue,
    setEnvValue,
    getEnvValue,
    getEnvValues,
    getEnvValueByPattern,
    initUserEnvMap,
    // [DEV ONLY]
    ...devOnly,
  };
});
